import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchServices, deleteService } from "../redux/servicesSlice";

const ServicesPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const { services } = useSelector((state) => state.services);
  const user = useSelector((state) => state.user.currentUser);

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const servicesPerPage = 10;
  const handleEdit = (service) => navigate(`/admin/services/edit/${service._id}`);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  useEffect(() => {
    dispatch(fetchServices());
  }, [dispatch]);

  useEffect(() => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    if (currentPage > 1) params.page = currentPage;
    setSearchParams(params);
  }, [searchTerm, currentPage, setSearchParams]);

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteService(serviceToDelete)).unwrap();
      dispatch(fetchServices());
      showToast("Yorum silindi", "info");
    } catch {
      showToast("Silme başarısız", "danger");
    } finally {
      setConfirmDelete(false);
      setServiceToDelete(null);
    }
  };

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, show: false })), 3000);
  };

  const filtered = useMemo(() => {
    let list = [...services];
    if (searchTerm) {
      list = list.filter(
        (t) =>
          t.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return list;
  }, [services, searchTerm]);

  const totalPages = Math.ceil(filtered.length / servicesPerPage);
  const visibleServices = filtered.slice(
    (currentPage - 1) * servicesPerPage,
    currentPage * servicesPerPage
  );

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
        <h5 className="fw-bold mb-0">Hizmetler</h5>

        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div className="input-group input-group-sm" style={{ minWidth: 150 }}>
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Ara..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          {(user?.role === "admin" || user?.role === "editor") && (
            <button
              type="button"
              className="btn btn-primary btn-sm rounded-3 d-flex align-items-center gap-1"
              onClick={() => navigate("/admin/services-editor")}
            >
              <i className="bi bi-plus-lg"></i> Yeni Hizmet
            </button>
          )}
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-hover table-sm align-middle">
          <thead>
            <tr>
              <th>ID</th>
              <th>ikon</th>
              <th>başlık</th>
              <th>açıklama</th>
              <th className="text-end">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {visibleServices.map((item) => (
              <tr key={item._id}>
                <td>{item._id.slice(-6)}</td>
                <td>{item.icon}</td>
                <td>{item.title}</td>
                <td>{item.content}</td>
                <td className="text-end">
                  {user?.role === "admin" && (
                    <>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => {
                          setServiceToDelete(item._id);
                          setConfirmDelete(true);
                        }}
                      >
                        <p>Sil</p>
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav className="d-flex justify-content-center mt-3" aria-label="Sayfalama">
          <ul className="pagination pagination-sm">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              >
                Önceki
              </button>
            </li>
            {[...Array(totalPages)].map((_, i) => (
              <li
                key={i}
                className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              >
                Sonraki
              </button>
            </li>
          </ul>
        </nav>
      )}

      {/* Delete Confirmation Modal */}
      <div
        className={`modal fade ${confirmDelete ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        aria-hidden={!confirmDelete}
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-dialog-centered" onClick={() => setConfirmDelete(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">Bu yorumu silmek istiyor musunuz?</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setConfirmDelete(false)}
              ></button>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setConfirmDelete(false)}
              >
                İptal
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      <div
        className={`toast align-items-center text-white bg-${toast.type} border-0 position-fixed bottom-0 start-50 translate-middle-x mb-4 ${toast.show ? "show" : "hide"}`}
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        style={{ zIndex: 1060, minWidth: 200 }}
      >
        <div className="d-flex">
          <div className="toast-body">{toast.message}</div>
          <button
            type="button"
            className="btn-close btn-close-white me-2 m-auto"
            onClick={() => setToast((t) => ({ ...t, show: false }))}
          ></button>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;
